import type { JSX } from 'react';

import { useKnownAvailableParams } from '@change-corgi/core/react/router';
import { Loader } from '@change-corgi/design-system-legacy/components/progressiveDisclosure';

import { NotFoundError } from 'src/shared/error';
import { withPrefetchedData } from 'src/shared/prefetch';
import type { PrefetchedUserDataProps } from 'src/shared/prefetch';

import { getMatchingRoute, useCurrentRoute } from 'src/app/pages/petition/shared/routes';
import type { UserInfo } from 'src/app/pages/petition/shared/types';
import { prefetchUserDataUserInfo, useUserInfo } from 'src/app/pages/petition/shared/userInfo';
import { isLoaded } from 'src/app/shared/utils/async';

/**
 * This is the initial entrypoint that all petition details routes render. It's mostly a pass-through to the component
 * specified in `route.additionalConfig`, but it checks the state of the Gamma experiment to decide whether to render
 * `additionalConfig.component` or `additionalConfig.gammaComponent`.
 *
 * Once we're ready to drop the old page entirely, this component and the `additionalConfig` logic can go away.
 */
export const PetitionDetailsSubRouteRenderer = withPrefetchedData(
	({ prefetchedUserData }: PrefetchedUserDataProps<UserInfo>): JSX.Element => {
		const { slug } = useKnownAvailableParams<{ slug: string }>();
		const userInfoState = useUserInfo(slug, prefetchedUserData);
		const currentRoute = useCurrentRoute();

		if (!currentRoute) {
			// Should be impossible because component is only rendered on a matching route.
			throw new NotFoundError();
		}

		if (!isLoaded(userInfoState)) {
			return <Loader size="m" my={256} mx="auto" />;
		}

		if (userInfoState.info.newPetitionPage) {
			const GammaSubRouteComponent = currentRoute.additionalConfig.gammaComponent;
			return <GammaSubRouteComponent userInfoState={userInfoState} />;
		}

		const SubRouteComponent = currentRoute.additionalConfig.component;
		return <SubRouteComponent userInfoState={userInfoState} />;
	},
	{
		prefetchName: __MODULE_HASH__,
		prefetchUserData: async (context) => prefetchUserDataUserInfo(context),
		prefetchDependencies: async ({ path, getUserData }) => {
			const route = getMatchingRoute(path);
			if (!route) {
				// Should be impossible because component is only rendered on a matching route.
				throw new NotFoundError();
			}

			// We have to wait for user data to be fetched to decide which version of the page to serve.
			// This type comes from the `prefetchUserData` function above, but is not inferred automatically.
			// eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion, @typescript-eslint/consistent-type-assertions
			const userData = (await getUserData()) as UserInfo;

			if (userData.newPetitionPage) {
				// Just prefetch gamma's dependencies
				return [route.additionalConfig.gammaComponent];
			}

			// Just prefetch old page's dependencies
			return [route.additionalConfig.component];
		},
	},
);
